'use client';

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { X, Volume2, BookOpen, Loader2, ChevronDown } from 'lucide-react';
import { Button } from './button';

interface VocabularyWord {
  name: string;
  trans: string[];
  usphone?: string;
  ukphone?: string;
}

interface VocabularyPreviewDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  dictionaryFile: string;
  categoryTitle: string;
}

export const VocabularyPreviewDrawer: React.FC<VocabularyPreviewDrawerProps> = ({
  isOpen,
  onClose,
  dictionaryFile,
  categoryTitle,
}) => {
  const [words, setWords] = useState<VocabularyWord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(50);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Virtual scrolling parameters
  const ITEMS_PER_PAGE = 50;
  // const MAX_INITIAL_RENDER = 100;

  const fetchVocabulary = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`/api/user-settings/dictionaries/${encodeURIComponent(dictionaryFile)}`);
      if (!response.ok) {
        throw new Error('Failed to load vocabulary data');
      }
      
      const data = await response.json();
      const parsed = Array.isArray(data)
        ? data
        : Array.isArray(data?.words)
        ? data.words
        : [];

      setWords(parsed);
      // Reset visible count for new data
      setVisibleCount(Math.min(ITEMS_PER_PAGE, parsed.length));
    } catch (err) {
      console.error('Error fetching vocabulary:', err);
      setError('Failed to load vocabulary preview');
    } finally {
      setLoading(false);
    }
  }, [dictionaryFile]);

  // Fetch vocabulary data when drawer opens
  useEffect(() => {
    if (isOpen && dictionaryFile) {
      fetchVocabulary();
    } else if (!isOpen) {
      // Reset state when drawer closes
      setVisibleCount(ITEMS_PER_PAGE);
    }
  }, [isOpen, dictionaryFile, fetchVocabulary]);

  const pronounceWord = (word: string) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(word);
      utterance.lang = 'en-US';
      utterance.rate = 0.8;
      speechSynthesis.speak(utterance);
    }
  };

  // Memoized visible words for performance
  const filteredWords = useMemo(() => {
    if (!searchQuery.trim()) {
      return words;
    }

    const query = searchQuery.trim().toLowerCase();
    return words.filter((word) => {
      if (!word) return false;
      const nameMatch = word.name?.toLowerCase().includes(query);
      const translationMatch = word.trans?.some((t) => t.toLowerCase().includes(query));
      return nameMatch || translationMatch;
    });
  }, [words, searchQuery]);

  const visibleWords = useMemo(() => {
    return filteredWords.slice(0, visibleCount);
  }, [filteredWords, visibleCount]);

  const loadMoreWords = () => {
    const newCount = Math.min(visibleCount + ITEMS_PER_PAGE, filteredWords.length);
    setVisibleCount(newCount);
  };

  const showAllWords = () => {
    // For large lists, limit to reasonable amount to prevent browser freeze
    const maxCount = Math.min(filteredWords.length, 1000);
    setVisibleCount(maxCount);
  };

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Custom scrollbar styles */}
      <style jsx>{`
        .vocabulary-scrollable {
          scrollbar-width: thin;
          scrollbar-color: hsl(var(--muted)) transparent;
        }
        .vocabulary-scrollable::-webkit-scrollbar {
          width: 6px;
        }
        .vocabulary-scrollable::-webkit-scrollbar-track {
          background: transparent;
        }
        .vocabulary-scrollable::-webkit-scrollbar-thumb {
          background-color: hsl(var(--muted));
          border-radius: 3px;
        }
        @media (hover: hover) {
          .vocabulary-scrollable::-webkit-scrollbar-thumb:hover {
            background-color: hsl(var(--muted-foreground));
          }
        }
      `}</style>
    <div
      className="fixed inset-0 z-50 flex justify-end"
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" />
      
      {/* Drawer */}
      <div
        className={`relative w-full max-w-md h-full bg-background shadow-2xl transform transition-transform duration-300 ease-out flex flex-col overflow-hidden ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle indicator */}
        <div className="flex justify-center py-2 bg-background border-b flex-shrink-0">
          <div className="w-12 h-1 bg-muted rounded-full"></div>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-background flex-shrink-0">
          <div className="flex items-center gap-3">
            <BookOpen className="h-5 w-5 text-primary" />
            <div>
              <h2 className="text-lg font-semibold text-foreground">{categoryTitle}</h2>
              <p className="text-sm text-muted-foreground">Vocabulary Preview</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="hover:bg-muted"
            aria-label="Close preview"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <div 
          className="flex-1 overflow-y-auto overscroll-contain px-4 pb-2 vocabulary-scrollable bg-background" 
          style={{ 
            WebkitOverflowScrolling: 'touch',
            height: '0', // Force flex-1 to work properly
            minHeight: '0'
          }}
        >
          {loading ? (
            // Loading state
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
              <p className="text-muted-foreground">Loading vocabulary preview...</p>
            </div>
          ) : error ? (
            // Error state
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="text-4xl mb-4">⚠️</div>
              <h3 className="text-lg font-medium text-foreground mb-2">Preview Unavailable</h3>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button onClick={fetchVocabulary} variant="outline" size="sm">
                Try Again
              </Button>
            </div>
          ) : words.length > 0 ? (
            // Vocabulary list
            <div className="space-y-3 min-h-0">
              <div className="flex items-center justify-between mb-4 sticky top-0 bg-background py-3 border-b border-border/50 z-10">
                <div className="flex items-center gap-2 w-full">
                  <div className="relative flex-1">
                    <input
                      value={searchQuery}
                      onChange={(event) => {
                        setSearchQuery(event.target.value);
                        setVisibleCount(ITEMS_PER_PAGE);
                      }}
                      placeholder="Search vocabulary..."
                      className="w-full rounded-lg border border-border/60 bg-background px-3 py-2 text-sm shadow-inner focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                      Showing {visibleWords.length} of {filteredWords.length} words
                    </p>
                  </div>
                </div>
                {/* <div className="flex gap-2">
                  {visibleCount < filteredWords.length && visibleCount < 1000 && (
                    <button
                      onClick={loadMoreWords}
                      className="text-xs text-primary bg-primary/10 hover:bg-primary/20 px-2 py-1 rounded transition-colors flex items-center gap-1"
                    >
                      <ChevronDown className="h-3 w-3" />
                      Load More
                    </button>
                  )}
                  {filteredWords.length > 100 && visibleCount < 1000 && (
                    <button
                      onClick={showAllWords}
                      className="text-xs text-muted-foreground bg-muted hover:bg-muted/70 px-2 py-1 rounded transition-colors"
                    >
                      Show More (up to 1000)
                    </button>
                  )}
                </div> */}
              </div>
              
              {visibleWords.map((word, index) => {
                const overallIndex = index + 1;
                const translations = (word.trans || []).map((t) => t?.trim()).filter(Boolean);

                return (
                  <div
                    key={`${word.name}-${overallIndex}`}
                    className="group relative mb-3 rounded-xl border border-border/60 bg-muted/30 p-4 shadow-sm transition hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md"
                  >
                    <div className="absolute -top-2 -left-2 rounded-full bg-primary/10 px-2 py-1 text-[11px] font-medium text-primary shadow-sm">
                      #{overallIndex}
                    </div>

                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="text-base font-semibold text-foreground tracking-tight">
                            {word.name}
                          </h3>
                          {(word.usphone || word.ukphone) && (
                            <button
                              onClick={() => pronounceWord(word.name)}
                              className="inline-flex items-center rounded-full bg-primary/10 px-2 py-1 text-[11px] font-medium text-primary shadow-sm transition hover:bg-primary/20"
                              aria-label={`Pronounce ${word.name}`}
                            >
                              <Volume2 className="mr-1 h-3.5 w-3.5" /> Speak
                            </button>
                          )}
                        </div>

                        {(word.usphone || word.ukphone) && (
                          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs font-medium text-muted-foreground">
                            {word.usphone && (
                              <span className="font-mono">US&nbsp;/ {word.usphone} /</span>
                            )}
                            {word.ukphone && (
                              <span className="font-mono">UK&nbsp;/ {word.ukphone} /</span>
                            )}
                          </div>
                        )}

                        <div className="mt-3 flex flex-wrap gap-2">
                          {translations.length ? (
                            translations.map((translation, idx) => (
                              <span
                                key={idx}
                                className="rounded-full bg-primary/10 px-2.5 py-1 text-sm text-foreground/90 shadow-sm ring-1 ring-primary/20"
                              >
                                {translation}
                              </span>
                            ))
                          ) : (
                            <span className="text-sm text-muted-foreground">No translation available</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {/* Load more section */}
                  {visibleCount < filteredWords.length && (
                <div className="mt-6 p-4 bg-muted/20 rounded-lg border text-center">
                  <p className="text-sm text-muted-foreground mb-3">
                    {filteredWords.length - visibleCount} more words available
                  </p>
                  <div className="flex gap-2 justify-center">
                    <button
                      onClick={loadMoreWords}
                      className="text-sm text-primary bg-primary/10 hover:bg-primary/20 px-3 py-1.5 rounded transition-colors flex items-center gap-1"
                    >
                      <ChevronDown className="h-4 w-4" />
                      Load {Math.min(ITEMS_PER_PAGE, filteredWords.length - visibleCount)} More
                    </button>
                    {filteredWords.length > 100 && visibleCount < 1000 && (
                      <button
                        onClick={showAllWords}
                        className="text-sm text-muted-foreground bg-muted hover:bg-muted/70 px-3 py-1.5 rounded transition-colors"
                      >
                        Show More (up to 1000)
                      </button>
                    )}
                  </div>
                </div>
              )}
              
              {/* Footer info */}
              <div className="mt-6 p-4 bg-primary/5 rounded-lg border-2 border-dashed border-primary/20">
                <p className="text-sm text-muted-foreground text-center">
                  {visibleCount >= filteredWords.length ? (
                    <>Complete vocabulary list with {filteredWords.length} words.</>
                  ) : (
                    <>Showing {visibleWords.length} of {filteredWords.length} words.</>
                  )}
                  <br />
                  Select this vocabulary to start learning.
                  {filteredWords.length > 1000 && visibleCount >= 1000 && (
                    <>
                      <br />
                      <span className="text-xs">
                        (Showing first 1000 words for performance)
                      </span>
                    </>
                  )}
                </p>
              </div>
            </div>
          ) : (
            // No words
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="text-4xl mb-4">📚</div>
              <h3 className="text-lg font-medium text-foreground mb-2">No Preview Available</h3>
              <p className="text-muted-foreground">This vocabulary file appears to be empty.</p>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
};
